import { useState, useEffect, useRef } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import getClassName from "../../utilities/getClassName";
import chevronDown from "../../assets/icons/chevron-down-big.svg";
import chevronUp from "../../assets/icons/chevron-up-big.svg";

const PassengerDropdown = ({
    id,
    icon,
    defaultValue = 1,
    value,
    min = 1,
    max = 99,
    validateForm,
    onFocus,
    skeleton,
    classNames = [],
    isOpen: controlledIsOpen,
    onToggle: controlledOnToggle,
}) => {
    const [currentValue, setCurrentValue] = useState(defaultValue);
    const [hasError, setHasError] = useState(false);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

    useEffect(() => {
        if (defaultValue !== undefined && defaultValue !== null) {
            setCurrentValue(defaultValue);
        }
    }, [defaultValue]);

    useEffect(() => {
        if (validateForm) {
            setHasError(!currentValue || currentValue < min);
        }
    }, [validateForm, currentValue, min]);

    useClickOutside([dropdownRef, buttonRef], () => {
        if (isOpen) {
            if (controlledOnToggle) {
                controlledOnToggle(id, false);
            } else {
                setInternalIsOpen(false);
            }
        }
    });

    const handleIncrement = (e) => {
        e.stopPropagation();
        if (currentValue < max) {
            const newValue = currentValue + 1;
            setCurrentValue(newValue);
            if (value) value(newValue);
            if (onFocus) onFocus();
        }
    };

    const handleDecrement = (e) => {
        e.stopPropagation();
        if (currentValue > min) {
            const newValue = currentValue - 1;
            setCurrentValue(newValue);
            if (value) value(newValue);
            if (onFocus) onFocus();
        }
    };

    const handleChange = (e) => {
        e.stopPropagation();
        const inputValue = e.target.value;

        if (inputValue === "") {
            setCurrentValue("");
            return;
        }

        const newValue = parseInt(inputValue);

        if (!isNaN(newValue)) {
            setCurrentValue(newValue);

            if (newValue >= min && newValue <= max) {
                if (value) value(newValue);
                if (onFocus) onFocus();
            }
        }
    };

    const handleBlur = () => {
        if (currentValue === "" || currentValue < min) {
            setCurrentValue(min);
            if (value) value(min);
        } else if (currentValue > max) {
            setCurrentValue(max);
            if (value) value(max);
        }
    };

    const toggleDropdown = () => {
        const newIsOpen = !isOpen;
        if (controlledOnToggle) {
            controlledOnToggle(id, newIsOpen);
        } else {
            setInternalIsOpen(newIsOpen);
        }
        if (!isOpen && onFocus) {
            onFocus();
        }
    };

    const classes = getClassName([
        "passenger-dropdown",
        hasError ? "passenger-dropdown--error" : "",
        skeleton ? "passenger-dropdown--skeleton" : "",
        ...classNames,
    ]);

    return (
        <fieldset className={classes}>
            <div className="passenger-dropdown__container">
                <button
                    ref={buttonRef}
                    type="button"
                    aria-label={`Passengers: ${currentValue}`}
                    aria-expanded={isOpen}
                    className={getClassName([
                        "passenger-dropdown__button",
                        isOpen && "passenger-dropdown__button--active",
                    ])}
                    onClick={toggleDropdown}
                >
                    {icon && <img src={icon} alt="" className="passenger-dropdown__icon" />}
                    <span className="passenger-dropdown__value">{currentValue}</span>
                    <img src={isOpen ? chevronUp : chevronDown} alt="" className="passenger-dropdown__chevron" />
                </button>

                {isOpen && (
                    <div ref={dropdownRef} className="passenger-dropdown__popoff">
                        <div className="passenger-dropdown__popoff__tail" />
                        <div className="passenger-dropdown__popoff__content">
                            <div className="passenger-dropdown__popoff__label">Passengers</div>
                            <div className="passenger-dropdown__popoff__controls">
                                <button
                                    type="button"
                                    className="passenger-dropdown__popoff__button passenger-dropdown__popoff__button--minus"
                                    onClick={handleDecrement}
                                    disabled={currentValue <= min}
                                >
                                    −
                                </button>
                                <input
                                    id={id}
                                    type="number"
                                    className="passenger-dropdown__popoff__input"
                                    value={currentValue}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    min={min}
                                    max={max}
                                />
                                <button
                                    type="button"
                                    className="passenger-dropdown__popoff__button passenger-dropdown__popoff__button--plus"
                                    onClick={handleIncrement}
                                    disabled={currentValue >= max}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default PassengerDropdown;
